import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { capitalCase } from 'change-case'
import { ArrowLeft, Check, Download, Pencil, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router'

import { isApproverOrHigherRole, isPrivilegedRole } from '@ankor-io/common/domain/iam/account'
import { fromCents } from '@ankor-io/common/domain/pay/currency'
import { Role } from '@ankor-io/common/types/account'
import type { CharterAttachmentPayload } from '@ankor-io/common/types/charter'
import type {
  AWXCardActivityEventPayload,
  AWXCardActivityEventStatus,
  FullTransactionPayload,
  VatCode,
} from '@ankor-io/common/types/transaction/transaction'
import type { TransactionPayload } from '@ankor-io/common/types/transaction/transaction'
import { PaymentMethod, TransactionStatus } from '@ankor-io/common/types/transaction/transaction'
import { formatCurrency } from '@ankor-io/common/util/currencies'
import { formatDate } from '@ankor-io/common/util/dates'
import { getStatusColour, getStatusText } from '@ankor-io/common/util/enum-to-string/transaction'
import {
  getPaymentStatusColour,
  getPaymentStatusIcon,
  getPaymentStatusText,
} from '@ankor-io/common/util/enum-to-string/transaction.payment.status'
import { getVatCodeText } from '@ankor-io/common/util/enum-to-string/vat'

import { useTransaction } from '@/hooks/useTransaction'
import { useUserContext } from '@/providers/user/useUserContext'
import { Badge } from '@/ui-components/Badge'
import { Button } from '@/ui-components/Button'
import { Chip } from '@/ui-components/Chip'
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '@/ui-components/Dropdown'
import { Heading } from '@/ui-components/Heading'
import Loading from '@/ui-components/Loading'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/ui-components/Table'

const TransactionDetailPage: React.FC = () => {
  const navigate = useNavigate()
  const { transactionId } = useParams()
  const { getAccessToken } = useKindeAuth()
  const { transaction, setTransaction } = useTransaction()
  // To avoid the unknown type casting
  const [awxTransactionData, setAwxTransactionData] = useState<AWXCardActivityEventPayload | null>(null)
  const { activeOrganizationId, activeConnectedAccountId, activeOrganizationRole, user } = useUserContext()

  const [isLoading, setIsLoading] = useState(true)
  const [isActioningTransaction, setIsActioningTransaction] = useState(false)
  const [attachments, setAttachments] = useState<CharterAttachmentPayload[]>([])

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true)
        const res = await fetch(
          `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/transaction/${transactionId}`,
          {
            headers: {
              Authorization: `Bearer ${await getAccessToken()}`,
            },
          },
        )

        if (!res.ok) {
          throw new Error(`Failed to fetch transaction: ${res.status}`)
        }

        const transactionData = (await res.json()) as FullTransactionPayload
        setTransaction(transactionData)
      } catch (error) {
        console.error('Error fetching transaction data:', error)
      } finally {
        setIsLoading(false)
      }
    }

    if (transaction?.id === transactionId) {
      setIsLoading(false)
    } else {
      fetchData()
    }

    const fetchFiles = async () => {
      if (!transactionId) {
        return
      }

      try {
        const res = await fetch(
          `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/transaction/${transactionId}/attachments`,
          {
            headers: {
              Authorization: `Bearer ${await getAccessToken()}`,
            },
          },
        )

        if (!res.ok) {
          throw new Error('Failed to fetch files')
        }

        const data = (await res.json()) as { attachments: CharterAttachmentPayload[] }
        setAttachments(data.attachments ?? [])
      } catch (error) {
        if (error instanceof Error) {
          console.error('Error fetching files:', error)
        }
      }
    }

    fetchFiles()
  }, [getAccessToken, transactionId, setTransaction, transaction, activeOrganizationId, activeConnectedAccountId])

  useEffect(() => {
    if (transaction?.awxActivityEvent) {
      setAwxTransactionData(transaction.awxActivityEvent.data as AWXCardActivityEventPayload)
    }
  }, [transaction?.awxActivityEvent])

  const downloadAttachment = async (attachmentId: string) => {
    try {
      const res = await fetch(
        `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/transaction/${transactionId}/attachments/${attachmentId}`,
        {
          headers: {
            Authorization: `Bearer ${await getAccessToken()}`,
          },
        },
      )

      if (!res.ok) {
        throw new Error('Failed to download attachment')
      }

      const data = (await res.json()) as { attachment: CharterAttachmentPayload }

      const blobResponse = await fetch(data.attachment.url)
      if (!blobResponse.ok) {
        throw new Error('Failed to fetch file content')
      }

      const blob = await blobResponse.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = data.attachment.name || 'download'
      a.style.display = 'none'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading attachment:', error)
    }
  }

  const handleEditTransaction = (transaction: TransactionPayload) => {
    setTransaction(transaction)
    navigate(`/transactions/${transaction.id}/edit`)
  }

  const handleApprove = (transaction: FullTransactionPayload | null) => {
    const approveTransaction = async () => {
      if (!transaction) {
        throw new Error('Transaction not found')
      }

      setIsActioningTransaction(true)

      try {
        // convert embarkationDate and disembarkationDate to ISO-8601 DateTime
        const res = await fetch(
          `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/transaction/${transaction.id}/status`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${await getAccessToken()}`,
            },
            method: 'POST',
            body: JSON.stringify({ status: TransactionStatus.APPROVED }),
          },
        )

        if (!res.ok) {
          throw new Error(`Failed to approve transaction.`)
        }

        const updatedTransaction = { ...transaction, status: TransactionStatus.APPROVED }
        setTransaction(updatedTransaction)
      } catch (error) {
        console.error('Error approving transactions:', error)
        throw new Error(`Failed to approve transaction.`)
      } finally {
        setIsActioningTransaction(false)
      }
    }

    toast.promise(approveTransaction, {
      loading: 'Approving expense...',
      success: 'Expense approved successfully.',
      error: 'Failed to approve expense.',
    })
  }

  const handleReject = (transaction: FullTransactionPayload | null) => {
    const rejectTransaction = async () => {
      if (!transaction) {
        throw new Error('Transaction not found')
      }

      setIsActioningTransaction(true)

      try {
        // convert embarkationDate and disembarkationDate to ISO-8601 DateTime
        const res = await fetch(
          `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/transaction/${transaction.id}/status`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${await getAccessToken()}`,
            },
            method: 'POST',
            body: JSON.stringify({ status: TransactionStatus.REJECTED }),
          },
        )

        if (!res.ok) {
          throw new Error(`Failed to reject transaction.`)
        }

        const updatedTransaction = { ...transaction, status: TransactionStatus.REJECTED }
        setTransaction(updatedTransaction)
      } catch (error) {
        console.error('Error rejecting transactions:', error)
        throw new Error(`Failed to reject transaction.`)
      } finally {
        setIsActioningTransaction(false)
      }
    }

    toast.promise(rejectTransaction, {
      loading: `Rejecting expense...`,
      success: `Expense rejected successfully.`,
      error: `Failed to reject expense.`,
    })
  }

  if (isLoading) {
    return <Loading />
  }

  if (!transaction) {
    return <div>Expense not found</div>
  }

  return (
    <>
      <Button
        plain
        className="mb-2"
        aria-label="Go back"
        onClick={() => navigate(-1)}
      >
        <ArrowLeft data-slot="icon" />
        Back
      </Button>

      <div className="flex justify-between md:items-center flex-col md:flex-row gap-x-2 gap-y-1">
        <div className="flex flex-col sm:flex-row sm:items-center gap-x-2 ">
          <Heading level={3}>View Expense {transaction.charter?.name && `for ${transaction.charter.name}`}</Heading>
        </div>

        {/* Actions */}
        {/* Is Admin or Owner, can always action */}
        {/* Is Spender or Approver, can only action their own transactions that are not approved */}
        {(isPrivilegedRole(activeOrganizationRole) ||
          ((activeOrganizationRole === Role.SPENDER || activeOrganizationRole === Role.APPROVER) &&
            transaction.createdBy === user?.id &&
            transaction.status !== TransactionStatus.APPROVED)) && (
          <div
            className="relative z-10 pointer-events-auto"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            <Dropdown>
              <DropdownButton
                color="dark"
                className="w-full sm:w-auto"
              >
                Actions
              </DropdownButton>
              <DropdownMenu anchor="bottom end">
                <DropdownItem
                  className="disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isActioningTransaction}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation()
                    handleEditTransaction(transaction)
                  }}
                >
                  <Pencil data-slot="icon" />
                  Edit
                </DropdownItem>
                {transaction.status !== TransactionStatus.APPROVED &&
                  transaction.status !== TransactionStatus.REJECTED &&
                  isApproverOrHigherRole(activeOrganizationRole) && (
                    <>
                      <DropdownItem
                        className="disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={isActioningTransaction}
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation()
                          handleApprove(transaction)
                        }}
                      >
                        <Check data-slot="icon" />
                        Approve
                      </DropdownItem>
                      <DropdownItem
                        className="disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={isActioningTransaction}
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation()
                          handleReject(transaction)
                        }}
                      >
                        <X data-slot="icon" />
                        Reject
                      </DropdownItem>
                    </>
                  )}
              </DropdownMenu>
            </Dropdown>
          </div>
        )}
      </div>

      {/* Expense Details */}
      <div className="space-y-6 mt-4">
        <div className="space-y-2">
          <Heading level={4}>Expense Details</Heading>
          <div className="flex flex-col sm:grid sm:grid-cols-2 md:grid-cols-3 gap-4 border rounded-lg p-4 border-gray-50 dark:border-gray-800">
            {/* Expense Status */}
            <div className="space-y-1">
              <div className="text-sm text-gray-500 dark:text-gray-400">Expense Status</div>
              <Badge color={getStatusColour(transaction.status)}>{getStatusText(transaction.status)}</Badge>
            </div>

            {/* Date */}
            <div className="space-y-1">
              <div className="text-sm text-gray-500 dark:text-gray-400">Date</div>
              <div>{formatDate(transaction.date)}</div>
            </div>

            {/* Vendor/Supplier */}
            <div className="space-y-1">
              <div className="text-sm text-gray-500 dark:text-gray-400">Vendor/Supplier</div>
              <div>{transaction.vendor || '-'}</div>
            </div>

            {/* Category */}
            <div className="space-y-1">
              <div className="text-sm text-gray-500 dark:text-gray-400">Category</div>
              {transaction.category && (
                <div className="mt-1">
                  <Chip
                    name={transaction.category.name}
                    colour={transaction.category.color}
                    icon={transaction.category.icon}
                  />
                </div>
              )}
              {!transaction.category && <div>-</div>}
            </div>

            {/* Payment Method */}
            <div className="space-y-1 md:mt-[0.1875rem]">
              <div className="text-sm text-gray-500 dark:text-gray-400">Payment Method</div>
              <div>{capitalCase(transaction.paymentMethod)}</div>
            </div>

            {/* Comments */}
            <div className="col-span-2 md:col-span-3 space-y-1">
              <div className="text-sm text-gray-500 dark:text-gray-400">Comments</div>
              <div className="whitespace-pre-wrap">{transaction.comments || '-'}</div>
            </div>

            {/* Amount */}
            <div className="space-y-1">
              <div className="text-sm text-gray-500 dark:text-gray-400">Amount</div>
              <div className="flex gap-x-2 mt-1">
                <div className="w-full">
                  {formatCurrency(
                    fromCents(transaction.originalAmountCents) || 0,
                    transaction.originalAmountCurrencyCode,
                  )}
                </div>
              </div>
            </div>

            {transaction.charter?.id && (
              <>
                {/* Conversion Rate */}
                <div className="space-y-1">
                  <div className="text-sm text-gray-500 dark:text-gray-400">Conversion Rate</div>
                  <div>{transaction.conversionRateStr || '-'}</div>
                </div>

                {/* Amount Converted */}
                <div className="space-y-1">
                  <div className="text-sm text-gray-500 dark:text-gray-400">Amount Converted</div>
                  <div>
                    {transaction.conversionRateStr
                      ? `${formatCurrency(
                          Number(fromCents(transaction.originalAmountCents)) * Number(transaction.conversionRateStr),
                          transaction.charterCurrencyCode,
                        )}`
                      : '-'}
                  </div>
                </div>
              </>
            )}

            {/* Tax */}
            <div className="space-y-1">
              <div className="text-sm text-gray-500 dark:text-gray-400">Tax</div>
              <div>{getVatCodeText(transaction.vatCode as VatCode) || '-'}</div>
            </div>

            {/* Tax Amount */}
            <div className="space-y-1">
              <div className="text-sm text-gray-500 dark:text-gray-400">Tax Amount</div>
              <div>{transaction.vat || '-'}</div>
            </div>
          </div>
        </div>

        {/* Transaction Details */}
        {(transaction.paymentMethod === PaymentMethod.ANKOR_PAY ||
          transaction.paymentMethod === PaymentMethod.TRANSFER) && (
          <div className="space-y-2">
            <Heading level={4}>Transaction Details</Heading>
            <div className="space-y-4 border rounded-lg p-4 border-gray-50 dark:border-gray-800">
              {/* TODO: Show payment status for transfers? */}
              {transaction.paymentMethod === PaymentMethod.ANKOR_PAY && (
                <div className="space-y-1">
                  <div className="text-sm text-gray-500 dark:text-gray-400">Payment Status</div>
                  {transaction.awxActivityEvent && (
                    <Chip
                      colour={getPaymentStatusColour(transaction.awxActivityEvent.status as AWXCardActivityEventStatus)}
                      name={getPaymentStatusText(transaction.awxActivityEvent.status as AWXCardActivityEventStatus)}
                      icon={getPaymentStatusIcon(transaction.awxActivityEvent.type)}
                    />
                  )}{' '}
                </div>
              )}

              {awxTransactionData && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Transaction Amount */}
                    <div className="space-y-1">
                      <div className="text-sm text-gray-500 dark:text-gray-400">Transaction Amount</div>
                      <div>
                        {formatCurrency(
                          fromCents(awxTransactionData.amountCents) || 0,
                          awxTransactionData.amountCurrencyCode,
                        )}
                      </div>
                    </div>

                    {/* Billed Amount */}
                    <div className="space-y-1">
                      <div className="text-sm text-gray-500 dark:text-gray-400">Billed Amount</div>
                      <div>
                        {formatCurrency(
                          fromCents(awxTransactionData.billingAmountCents) || 0,
                          awxTransactionData.billingCurrencyCode,
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Card Number */}
                    <div className="space-y-1">
                      <div className="text-sm text-gray-500 dark:text-gray-400">Card</div>
                      <div>{awxTransactionData.card?.maskedCardNumber || '-'}</div>
                    </div>

                    {/* Card Nickname */}
                    <div className="space-y-1">
                      <div className="text-sm text-gray-500 dark:text-gray-400">Card Nickname</div>
                      <div>{awxTransactionData.card?.nickName || '-'}</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Merchant */}
                    <div className="space-y-1">
                      <div className="text-sm text-gray-500 dark:text-gray-400">Merchant</div>
                      <div>{awxTransactionData.merchant?.name || '-'}</div>
                    </div>

                    {/* Merchant Country */}
                    <div className="space-y-1">
                      <div className="text-sm text-gray-500 dark:text-gray-400">Merchant Country</div>
                      <div>{awxTransactionData.merchant?.country || '-'}</div>
                    </div>

                    {/* Merchant City */}
                    <div className="space-y-1">
                      <div className="text-sm text-gray-500 dark:text-gray-400">Merchant City</div>
                      <div>{awxTransactionData.merchant?.city || '-'}</div>
                    </div>
                  </div>
                </>
              )}

              {/* Fees */}
              <div className="space-y-1">
                <div className="text-sm text-gray-500 dark:text-gray-400">Fees</div>
                {awxTransactionData?.fees.length ? (
                  <div>
                    {awxTransactionData?.fees.map((fee, index) => {
                      return (
                        <div key={`${index}-${fee.type}-${fee.amountCents}`}>
                          {fee.type}: {formatCurrency(fromCents(fee.amountCents) || 0, fee.currencyCode)}
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <div>-</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Files and Documents */}
        <div className="space-y-2">
          <Heading level={4}>Files and Documents</Heading>
          <div className="space-y-4 border rounded-lg p-4 border-gray-50 dark:border-gray-800">
            {attachments.length === 0 ? (
              <div className="text-sm text-gray-500 dark:text-gray-400">No files or documents found</div>
            ) : (
              <div className="overflow-x-auto">
                <Table className="max-w-[calc(100vw-7rem)] lg:max-w-[calc(100vw-24rem)]">
                  <TableHead>
                    <TableRow>
                      <TableHeader>Filename</TableHeader>
                      <TableHeader>Uploaded Date</TableHeader>
                      <TableHeader></TableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {attachments.map((attachment) => (
                      <TableRow key={attachment.id}>
                        <TableCell>
                          <span className="text-sm text-gray-500 dark:text-gray-400">{attachment.name}</span>
                        </TableCell>
                        <TableCell>{formatDate(attachment.uploadedAt)}</TableCell>
                        <TableCell>
                          <div className="flex items-center justify-around gap-2">
                            <Button
                              plain
                              onClick={() => downloadAttachment(attachment.id)}
                              data-testid={`download-button-${attachment.id}`}
                            >
                              <Download data-slot="icon" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>

        {/* TODO: Add approval history */}
        <div className="space-y-2">
          <Heading level={4}>Approval History</Heading>
          <div className="space-y-4 border rounded-lg p-4 border-gray-50 dark:border-gray-800">
            <div className="text-sm text-gray-500 dark:text-gray-400">No approval history found</div>
          </div>
        </div>
      </div>
    </>
  )
}

export default TransactionDetailPage
